import { useEffect, useState } from "react";
import { useBreakpointValue, Box, Flex, Slide, Button, Link } from "@chakra-ui/react";
import { Airhelp as AirHelpLogo } from "@airhelp/react";
import FlagBox from "components/LanguageSwitcher/FlagBox";
import { MAX_GRID_WIDTH } from "config/styleConsts";
import { useLanguage } from "contexts/LocalizationContext";
import { useModal } from "contexts/ModalContext";
import { useFlagBoxSettings } from "contexts/SettingsContext";
import { useMobileMenuStore } from "stores/MobileMenuStore";
import { useReturningUsersNotification, useDetectScrollDirection } from "hooks";
import throttle from "lodash/throttle";
import { TopBanner } from "modules/common/components/TopBanner";
import NextLink from "next/link";
import { HamburgerMenuIcon } from "styles/icons";
import { useExperimentalTopMenu } from "hooks/useMenu";
import ReturningUsersNotification from "../ReturningUsersNotification";
import { TOP_MENU_LANG_MODAL_OPEN_EVENT_UA, TOP_MENU_LANG_MODAL_OPEN_EVENT } from "../../trackingConsts";
import type { HeaderNavProps } from "./HeaderNav.d";
import { MobileMenuContent } from "./MenuItems";
import { HeaderNavExperiment, MenuItemsExperiment, useTopMenuExperiment } from "./experiment";

export const HeaderNavOriginal = ({
  logo: Logo = AirHelpLogo,
  menuItems,
  headerButtons,
  topBannerText = "",
  topBannerButtonText = "",
  subMenu = null,
}: HeaderNavProps) => {
  const [slideOpen, setSlideOpen] = useState(true);
  const [smallNav, setSmallNav] = useState(false);
  const [position, setPosition] = useState<"sticky" | "fixed">("fixed");
  const language = useLanguage();
  const { openModal } = useModal();
  const { openMobileMenu } = useMobileMenuStore();
  const hideFlagBox = useFlagBoxSettings();
  const { isOpen: isNotificationVisible } = useReturningUsersNotification();

  const scrollDirection = useDetectScrollDirection("80");

  const navBoxShadow = smallNav ? "1px 4px 10px rgba(203, 202, 209, 0.25)" : "none";

  const logoSmallWidth = "95px";
  const logoLargeWidth = "118px";

  const logoWidth = { base: logoSmallWidth, lg: smallNav ? logoSmallWidth : logoLargeWidth };

  const topBannerComponent = topBannerText && topBannerButtonText && (
    <TopBanner bannerText={topBannerText} buttonText={topBannerButtonText} />
  );

  const navOnScroll = () => {
    setSmallNav(window.pageYOffset > 0);
  };

  const throttledResizeListener = throttle(navOnScroll, 50);

  useEffect(() => {
    setPosition(isNotificationVisible ? "sticky" : "fixed");
  }, [isNotificationVisible]);

  useEffect(() => {
    setSlideOpen(scrollDirection !== "down");

    window.addEventListener("scroll", throttledResizeListener);

    return () => window.removeEventListener("scroll", throttledResizeListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollDirection]);

  return (
    <>
      <ReturningUsersNotification />
      <Slide
        initial="visible"
        direction="top"
        in={useBreakpointValue({ base: slideOpen, lg: true })}
        style={{ zIndex: 4, position }}
      >
        {topBannerComponent}
        <Flex
          wrap="wrap"
          px={{ base: 4, xl: 0 }}
          color="greyscale.600"
          backgroundColor="white"
          py={4}
          mb={-20}
          boxShadow={navBoxShadow}
        >
          <Flex
            width="full"
            as="nav"
            role="menubar"
            maxWidth="fit-content"
            minW={{ base: "full", xl: MAX_GRID_WIDTH }}
            mx="auto"
            justify={{ base: "space-between", lg: "unset" }}
          >
            <Link as={NextLink} href={`/${language}/`} passHref>
              <Logo color="primary.900" minW={logoWidth} transition="0.2s" minH={{ base: "35px", lg: "40px" }} />
            </Link>

            <Box
              display={{ base: "none", lg: "flex" }}
              width={{ base: "full", md: "auto" }}
              justifyContent="flex-end"
              alignItems="center"
              flexGrow={1}
              mr={hideFlagBox && 5}
            >
              {menuItems}
            </Box>

            {!hideFlagBox && (
              <Box ml={{ base: "auto", lg: 1, "2xl": 4 }} mr={{ base: 3, lg: 0 }}>
                <FlagBox
                  openModal={openModal}
                  gaEvent={TOP_MENU_LANG_MODAL_OPEN_EVENT}
                  uaEvent={TOP_MENU_LANG_MODAL_OPEN_EVENT_UA}
                />
              </Box>
            )}

            <Box display={{ base: "none", lg: "flex" }} mt={{ base: 4, md: 0 }} ml={{ lg: 1, xl: 8 }}>
              {headerButtons}
            </Box>

            {subMenu && <Box display={{ base: "block", md: "none" }}>{subMenu}</Box>}

            <Button
              variant="unstyled"
              _hover={{}}
              _active={{}}
              display={{ base: "flex", lg: "none" }}
              onClick={() => openMobileMenu(<MobileMenuContent headerButtons={headerButtons} menuItems={menuItems} />)}
              data-testid="mobile-menu-button"
              aria-label="Open site menu"
            >
              <HamburgerMenuIcon color="primary.900" w={6} h={4} />
            </Button>
          </Flex>
        </Flex>
      </Slide>
    </>
  );
};

export const HeaderNav = (props: HeaderNavProps) => {
  const language = useLanguage();
  const experimentVariant = useTopMenuExperiment();
  const topMenuExperiment = useExperimentalTopMenu(language);

  if (experimentVariant) {
    return <HeaderNavExperiment {...props} menuItems={<MenuItemsExperiment menu={topMenuExperiment} />} />;
  }
  return <HeaderNavOriginal {...props} />;
};
